import { ALL_APP_LOCALES, DEFAULT_LOCALE, type AppLocale } from "@/lib/i18n/constants";
import { localizedPath } from "@/lib/seo/url";

export const BOOKING_URL = "https://calendar.app.google/DE3PeanocpDsjN47A";
export const CONTACT_HANDOFF_HASH = "#brief-form";

export type BookingCtaSurface =
  | "header"
  | "hero"
  | "mobile_bottom"
  | "footer"
  | "audience_card"
  | "service_card"
  | "funnel_planner";

type BookingCtaAttribution = {
  campaign: string;
  content: string;
  medium: BookingCtaSurface;
};

export const EXTERNAL_BOOKING_LINK_ATTRS = {
  target: "_blank",
  rel: "noopener noreferrer",
} as const;

export const bookingLinkAttrs = EXTERNAL_BOOKING_LINK_ATTRS;

const localePrefixPattern = new RegExp(`^/(${ALL_APP_LOCALES.join("|")})(?=/|$)`, "i");

type WhatsAppContext = {
  pageName: string;
  message: string;
};

const PAGE_CONTEXTS: readonly {
  match: (pathname: string) => boolean;
  context: WhatsAppContext;
}[] = [
  {
    match: (pathname) => pathname === "/",
    context: {
      pageName: "homepage",
      message: "Hi, give me more info about SEOH.",
    },
  },
  {
    match: (pathname) => pathname === "/services" || pathname.startsWith("/services/"),
    context: {
      pageName: "services page",
      message: "Hi, I want to scope SEO or GEO work.",
    },
  },
  {
    match: (pathname) => pathname === "/geo-aeo-ai-search" || pathname.startsWith("/ai-search/"),
    context: {
      pageName: "AI search page",
      message: "Hi, I want more info about AI search visibility.",
    },
  },
  {
    match: (pathname) => pathname === "/adult" || pathname.startsWith("/adult/"),
    context: {
      pageName: "adult compliance page",
      message: "Hi, I want to discuss lawful 18+ compliance-first marketing.",
    },
  },
  {
    match: (pathname) => pathname === "/white-label",
    context: {
      pageName: "white-label page",
      message: "Hi, I want to discuss white-label SEO support.",
    },
  },
  {
    match: (pathname) => pathname === "/franchise-multi-location",
    context: {
      pageName: "franchise page",
      message: "Hi, I want to discuss multi-location SEO support.",
    },
  },
  {
    match: (pathname) => pathname === "/ai-avatar-marketing",
    context: {
      pageName: "AI avatar page",
      message: "Hi, I want more info about AI avatar support.",
    },
  },
  {
    match: (pathname) => pathname === "/ai-video-production" || pathname === "/ai-avatar-video",
    context: {
      pageName: "AI video page",
      message: "Hi, I want more info about AI video support.",
    },
  },
  {
    match: (pathname) => pathname === "/resources" || pathname.startsWith("/resources/"),
    context: {
      pageName: "resources page",
      message: "Hi, I want help choosing the right SEOH resource.",
    },
  },
  {
    match: (pathname) => pathname === "/packages",
    context: {
      pageName: "packages page",
      message: "Hi, I want to discuss a quote-only scope.",
    },
  },
  {
    match: (pathname) => pathname === "/portfolio",
    context: {
      pageName: "portfolio page",
      message: "Hi, I want to discuss proof-sensitive SEO work.",
    },
  },
  {
    match: (pathname) => pathname === "/case-studies" || pathname.startsWith("/case-studies/"),
    context: {
      pageName: "case studies page",
      message: "Hi, I want to discuss a proof-backed scope.",
    },
  },
  {
    match: (pathname) => pathname === "/contact",
    context: {
      pageName: "contact page",
      message: "Hi, I want to start a fit review.",
    },
  },
];

const stripLocalePrefix = (pathname: string) => pathname.replace(localePrefixPattern, "") || "/";

const localeFromPathname = (pathname?: string | null): AppLocale => {
  const [, locale] = pathname?.match(localePrefixPattern) ?? [];

  return ALL_APP_LOCALES.includes(locale as AppLocale) ? (locale as AppLocale) : DEFAULT_LOCALE;
};

const toTitle = (value: string) =>
  value
    .split(/[-/]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const normalizePathname = (pathname?: string | null) => {
  if (!pathname) {
    return "/";
  }

  const [pathOnly] = pathname.split(/[?#]/);
  const withoutTrailingSlash = pathOnly.length > 1 ? pathOnly.replace(/\/+$/, "") : pathOnly;
  return stripLocalePrefix(withoutTrailingSlash || "/");
};

export const getWhatsAppContactContext = (pathname?: string | null): WhatsAppContext => {
  const normalizedPathname = normalizePathname(pathname);
  const matchedContext = PAGE_CONTEXTS.find(({ match }) => match(normalizedPathname));

  if (matchedContext) {
    return matchedContext.context;
  }

  if (normalizedPathname.startsWith("/industries/")) {
    const industryName = toTitle(normalizedPathname.replace(/^\/industries\//, ""));
    return {
      pageName: `${industryName} industry page`,
      message: `Hi, I want more info about SEOH support for ${industryName}.`,
    };
  }

  if (normalizedPathname.startsWith("/compare/")) {
    const comparisonName = toTitle(normalizedPathname.replace(/^\/compare\//, ""));
    return {
      pageName: `${comparisonName} comparison page`,
      message: "Hi, I want help comparing the right SEO or GEO approach.",
    };
  }

  const fallbackName = normalizedPathname === "/" ? "homepage" : `${toTitle(normalizedPathname)} page`;

  return {
    pageName: fallbackName,
    message: "Hi, give me more info about this SEOH page.",
  };
};

export const buildContactHandoffUrl = (locale: AppLocale = DEFAULT_LOCALE) => `${localizedPath(locale, "/contact")}${CONTACT_HANDOFF_HASH}`;

export const buildWhatsAppContactUrl = (pathname?: string | null) => buildContactHandoffUrl(localeFromPathname(pathname));

export function bookingCalendarHref(_attribution: BookingCtaAttribution): string {
  void _attribution;
  return BOOKING_URL;
}
